import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import moment from 'moment';

/**
 * Returns a string that can be used for <input type="datetime-local">
 */
export function toDateTimeLocal(date, withTime) {
  if (typeof date === 'string') {
    date = new Date(date);
  }
  if (Number.isNaN(date.valueOf())) {
    return '';
  }
  var ten = function ten(i) {
    return (i < 10 ? '0' : '') + i;
  };
  var YYYY = date.getFullYear();
  var MM = ten(date.getMonth() + 1);
  var DD = ten(date.getDate());
  var HH = ten(date.getHours());
  var II = ten(date.getMinutes());
  var datePart = YYYY + '-' + MM + '-' + DD;
  if (withTime) {
    return datePart + 'T' + HH + ':' + II + ':00';
  }
  return datePart;
}

/**
 *
 * @param {Date | string} date
 * the date to format
 *
 * @param {boolean} opts.time
 * whether to include the time
 * @param {boolean} opts.longDate
 * whether to put the date in long format
 */
export function formatDateSimple(date) {
  var opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  if (!date) {
    return '';
  }
  opts = _objectSpread({
    time: false
  }, opts);
  date = moment(date);
  var lengthCode = opts.longDate ? 'LL' : 'l';
  var code = opts.time ? "".concat(lengthCode, " LT") : lengthCode;
  return date.format(code);
}

/**
 * Parses a string into a date if it's a string.
 * If `undefined, returns `undefined`. Intended for use in `Model.parse`.
 * If `null`, returns `null`.
 * If an invalid date, returns `undefined`.
 * @param value
 */
export function maybeParseDate(value) {
  // For some reason, we have to force these conversions.
  // The caller signature is correct though.
  if (value === null) {
    return null;
  }
  if (value === '' || value === undefined) {
    return undefined;
  }
  var result = new Date(value);
  if (Number.isNaN(result.getTime())) {
    return undefined;
  }
  return result;
}

/**
 * Returns a "timeago" style string from a date (and an optional "now").
 *
 * @param {Date} date
 * @param {keyof UnitFormats | Date} style Can be `short` (2s) or `long` (2 seconds)
 * @param now
 *
 * @returns {string}
 */
export function timeago(date, style) {
  var now = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : new Date();
  if (style instanceof Date) {
    now = style;
    style = 'short';
  }
  var diff = now.getTime() - date.getTime();
  return formatDuration(diff, style || 'short');
}

/**
 * Returns a formatted month and day string with a suffix appropriate for the last digit from a date.
 *
 * @param {Date} date
 *
 * @returns {string}
 */
export function formatMonthAndDay(date) {
  var dateString = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric'
  }).format(date);
  var penultimateChar = dateString.charAt(dateString.length - 2);
  var ultimateChar = dateString.charAt(dateString.length - 1);
  var ordinalSuffix = function ordinalSuffix(ordinal) {
    switch (ordinal) {
      case '1':
        return 'st';
      case '2':
        return 'nd';
      case '3':
        return 'rd';
      case '4':
      case '5':
      case '6':
      case '7':
      case '8':
      case '9':
      case '0':
        return 'th';
      default:
        return null;
    }
  };
  if (penultimateChar === '1') {
    return dateString + 'th';
  }
  return dateString + ordinalSuffix(ultimateChar);
}

/**
 * Returns a formatted 12-hour time string from a date.
 *
 * @param {Date} date
 *
 * @returns {string}
 */
export function formatTime(date) {
  var timeString = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).format(date);
  return timeString;
}

/**
 * Formats a duration.
 *
 * @param {number} durationMs
 * @param {"short" | "long"} style
 * @param {string} nowText
 */
export function formatDuration(durationMs, style) {
  var nowText = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'now';
  var seconds = durationMs / 1000;
  var unitFormat = UnitFormats[style];
  return seconds < 5 ? nowText : seconds < 60 ? formatWithUnit(seconds, unitFormat.s) : seconds < 60 * 60 ? formatWithUnit(seconds / 60, unitFormat.m) : seconds < 60 * 60 * 24 ? formatWithUnit(seconds / (60 * 60), unitFormat.h) : seconds < 60 * 60 * 24 * 31 ? formatWithUnit(seconds / (60 * 60 * 24), unitFormat.d) : seconds < 60 * 60 * 24 * 365 ? formatWithUnit(seconds / (60 * 60 * 24 * 31), unitFormat.mo) : formatWithUnit(seconds / (60 * 60 * 24 * 365), unitFormat.y);
}
function formatWithUnit(value, unitPair) {
  var floored = Math.floor(value);
  var suffix = floored === 1 ? unitPair[0] : unitPair[1];
  return "".concat(floored).concat(suffix);
}
var UnitFormats = {
  short: {
    s: ['s', 's'],
    m: ['m', 'm'],
    h: ['h', 'h'],
    d: ['d', 'd'],
    mo: ['mo', 'mo'],
    y: ['y', 'y']
  },
  long: {
    s: [' second', ' seconds'],
    m: [' minute', ' minutes'],
    h: [' hour', ' hours'],
    d: [' day', ' days'],
    mo: [' month', ' months'],
    y: [' year', ' years']
  }
};
//# sourceMappingURL=dateUtil.js.map
